import { CloseRasnIcon } from '@components/icon'
import { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { useSearchRasnFamiliesQuery, usePerformRasnFamilyActionMutation } from 'api/rasn-family'
import { type ConfirmationConfig, TableView } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { usePaginatedData } from 'components/tableview/usePaginatedData'
import { useTableState } from 'components/tableview/useTableState'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch } from 'redux/store'
import { type RasnFamily, RasnFamilyStatus, type QueryResult } from 'types'
import { getEnumValues } from 'utils/utils'

const FILTER_INPUT_DELAY_MS = 500

export const RasnFamilyOverview = () => {
  const tableState = useTableState()
  const columnFiltersTransformer = (filters: ColumnFiltersState): ColumnFiltersState => filters.map(filter => {
    if (filter.id === 'id') {
      return { id: 'number', value: filter.value }
    } else if (filter.id === 'partnerName') {
      return { id: 'customerName', value: filter.value }
    } else if (filter.id === 'created') {
      return { id: 'timestamp', value: filter.value }
    }
    return filter
  })
  const sortingTransformer = (sorting: SortingState): SortingState => sorting.map(sort => {
    if (sort.id === 'id') {
      return { id: 'number', desc: sort.desc }
    } else if (sort.id === 'partnerName') {
      return { id: 'customerName', desc: sort.desc }
    } else if (sort.id === 'created') {
      return { id: 'timestamp', desc: sort.desc }
    }
    return sort
  })
  const { data, loading } = usePaginatedData<QueryResult<RasnFamily>>(useSearchRasnFamiliesQuery, tableState.pagination, tableState.columnFilters, tableState.sorting, undefined, columnFiltersTransformer, sortingTransformer)
  const navigate = useNavigate()
  const [performRasnFamilyAction] = usePerformRasnFamilyActionMutation()

  const handleCloseRasnFamily = async (rasnFamily: RasnFamily) => {
    if (rasnFamily.status === RasnFamilyStatus.CLOSED) {
      dispatch(showSnackbar({
        message: `Family RSO ${rasnFamily.id} is already closed.`,
        status: 'error'
      }))
      return
    }
    const result = await performRasnFamilyAction({ rasnFamilyId: rasnFamily.id, action: 'close' })
    if ('data' in result) {
      dispatch(showSnackbar({
        message: `All RASNs in the Family RSO ${rasnFamily.id} have been closed.`,
        status: 'success'
      }))
    }
  }

  const columns = useMemo(() => [
    { id: 'id', header: 'RASN Family ID', type: ColumnType.TEXT, columnWidth: 12 },
    { id: 'partnerName', header: 'Partner Name', type: ColumnType.TEXT, columnWidth: 20 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(RasnFamilyStatus), columnWidth: 8, enableSorting: false },
    { id: 'totalRasns', header: 'Total RASNs', type: ColumnType.NUMBER, columnWidth: 8, enableColumnFilter: false, enableSorting: false },
    { id: 'created', header: 'Created', type: ColumnType.DATE, columnWidth: 12 },
  ], [])

  const actionsWithConfirmation: ConfirmationConfig[] = [
    {
      title: 'Unlock RASN Family',
      message: 'Are you sure you want to unlock this RASN Family?',
      icon: 'Unlock',
      confirmText: 'Unlock',
      show: (row: RasnFamily) => row.status === RasnFamilyStatus.OPEN,
      onClick: (row: RasnFamily) => { dispatch(showSnackbar({ message: 'Unlocking RASN Families is not yet implemented.', status: 'info' })) }
    },
    {
      title: 'Close Family RSO',
      message: 'Are you sure you want to close this Family RSO? This will close all linked RASNs.',
      icon: CloseRasnIcon,
      confirmText: 'Close',
      show: (row: RasnFamily) => row.status !== RasnFamilyStatus.CANCELLED,
      onClick: (row: RasnFamily) => { handleCloseRasnFamily(row) }
    }
  ]

  return (
    <TableView
      columns={columns}
      data={data?.results ?? []}
      rowCount={data?.totalCount ?? 0}
      tableState={tableState}
      textFilterDelay={FILTER_INPUT_DELAY_MS}
      loading={loading}
      actions={[
        {
          label: 'View',
          icon: 'Show',
          hoverText: 'RASN Family details',
          onClick: (row: RasnFamily) => { navigate(`/rasn-family/${row.id}`) }
        }
      ]}
      actionsWithConfirmation={actionsWithConfirmation}
    />
  )
}
